<template>
  <v-card>
    <v-expansion-panels>
      <v-expansion-panel>
        <v-expansion-panel-title class="pa-0 pe-3">
          <v-card-title>
            {{ t('custom_message_frontend') }}
          </v-card-title>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row class="mt-0">
            <v-col cols="auto" class="border-e-md pt-0 d-flex flex-column">
              <v-row>
                <v-col>
                  <v-row no-gutters class="flex-shrink-1">
                    <v-col class="d-flex flex-shrink-1 align-content-start">
                      <v-switch :label="t('enable_feature')" color="primary" v-model="enableFeature" hide-details/>
                    </v-col>
                  </v-row>
                  <v-row no-gutters>
                    <v-col>
                      <v-select
                        :label="t('default_lang')"
                        density="compact"
                        :items="langSupported.map((el) => el.toUpperCase())"
                        v-model="defaultLocale"
                        :disabled="!enableFeature"
                      />
                    </v-col>
                  </v-row>
                  <v-row no-gutters>
                    <v-col class="mb-4">
                      <p>{{ t('language_loaded') }}:</p>
                      <p>{{ loadedLangs }}</p>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col class="align-self-end">
                  <v-btn variant="outlined" @click="submit">{{ t('save_settings_and_tabs') }}</v-btn>
                </v-col>
              </v-row>
            </v-col>
            <v-col class="pt-0 d-flex flex-column">
              <v-tabs v-model="tab" density="compact" color="primary" :disabled="!enableFeature">
                <v-tab v-for="lang in langSupported" :value="lang">
                  {{ lang }}
                </v-tab>
              </v-tabs>
              <v-textarea
                v-model="customMessage[tab]"
                hide-details
                :disabled="!enableFeature"
                auto-grow
                clearable
              />
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-card>
</template>

<script setup>
import { ref, toRefs, toRaw, watch, onMounted, computed } from "vue";
import { modifyPatchPark } from "../../remote/park.js";
import { removeNullEmptyFromObj } from "../../utils.js";
import { useI18n } from "vue-i18n";

const { t } = useI18n({ inheritLocale: true, useScope: 'local' });
const langSupported = ['en', 'it', 'fr', 'nl', 'de'];

// General State
const useAlerts = defineModel('useAlerts');
const loadingOverlay = defineModel('loadingOverlay');
const props = defineProps(['originalPark']);
const { originalPark } = toRefs(props);
const emit = defineEmits(['updated']);

// Internal State
const tab = ref('en');

// Park Data
const enableFeature = ref(false);
const customMessage = ref({});
const defaultLocale = ref(null);

const loadedLangs = computed(() => {
  return Object.keys(customMessage.value).map(key => key.toUpperCase()).join(', ') || "None";
});

function setFromPark(park) {
  enableFeature.value = park['show_custom_message'] || false;
  customMessage.value = park['custom_message'] || {};
  defaultLocale.value = park['custom_message_fallback_lang'].toUpperCase() || 'EN';
}

async function submit() {
  let payload = {};
  if (enableFeature.value) {
    payload['show_custom_message'] = true;
    let msgDict = toRaw(customMessage.value);
    // Remove from the dict every appearance of null and empty string
    removeNullEmptyFromObj(msgDict);
    payload['custom_message'] = msgDict;
    payload['custom_message_fallback_lang'] = String(defaultLocale.value).toLowerCase();
  } else {
    payload['show_custom_message'] = false;
  }
  console.log(payload);
  loadingOverlay.value = true;
  try {
    await modifyPatchPark(useAlerts, payload, originalPark.value.id);
    emit('updated');
  } catch (e) {
    useAlerts.value.errorAlert('Error during Park save', e.toString());
  } finally {
    loadingOverlay.value = false;
  }
}

watch(originalPark, setFromPark);

onMounted(() => {
  if (originalPark.value !== null) {
    setFromPark(originalPark.value);
  }
});


</script>

<i18n>
{
  "en": {
    "custom_message_frontend": "Custom Message in Frontend",
    "enable_feature": "Enable Feature",
    "default_lang": "Default Lang",
    "language_loaded": "Language loaded",
    "save_settings_and_tabs": "Save settings\nand all tabs"
  },
  "it": {
    "custom_message_frontend": "Messaggio Personalizzato nel Frontend",
    "enable_feature": "Abilita Funzionalità",
    "default_lang": "Lingua Predefinita",
    "language_loaded": "Lingue caricate",
    "save_settings_and_tabs": "Salva impostazioni\ne tutte le schede"
  },
  "fr": {
    "custom_message_frontend": "Message Personnalisé dans le Frontend",
    "enable_feature": "Activer la Fonctionnalité",
    "default_lang": "Langue par Défaut",
    "language_loaded": "Langues chargées",
    "save_settings_and_tabs": "Enregistrer les paramètres\net tous les onglets"
  }
}
</i18n>

<style scoped>

</style>
